import 'isomorphic-fetch';
import moment from 'moment';
import AuthService from '../core/AuthService';
import AppConfig from './AppConfig';

const GeneralError = (response: any) => {
  //CommonResponse wrapper
  if (response.ErrorThrown) {
    switch (response.ErrorType) {
      case 'MESSAGE':
        alert(response.ResponseDescription);
    }
    throw response.ResponseDescription;
  }
  //ServiceStack wrapper
  else if (response.ResponseStatus) {
    switch (response.ResponseStatus.ErrorCode) {
      case 'KnownError':
      case 'SqlException':
      default:
        console.log(response);
        console.log(response.ResponseStatus.StackTrace);
        if (response.ResponseStatus.ErrorCode === 'TokenException') {
          AuthService.OpenLogin();
        } else {
          alert(response.ResponseStatus.ErrorCode + '.\n' + response.ResponseStatus.Message);
        }
    }
    throw response.ResponseStatus.Message;
  }
  //Other
  else {
    switch (response.status) {
      case 401:
        AuthService.OpenLogin();
        throw new Error('Your session has expired. Log in again');
    }
  }
  throw response;
};

const Request = async (method: string, endpoint: string, data: any, BaseURL?: string) => {
  if (AuthService.auth == null) AuthService.fillAuthData();
  if (!AuthService.auth || !AuthService.auth.user) throw new Error('User not signed in.');

  const config: RequestInit = {
    method: method,
    mode: 'cors',
    // cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${AuthService.auth.user.BearerToken}`
    },
    body: null
  };
  if (['POST', 'PUT', 'DELETE'].includes(method)) config.body = JSON.stringify(data);
  let response = await fetch((BaseURL || AppConfig.BaseURL) + endpoint, config);
  if (response) {
    if (!response.ok) throw await response.json();
    if (response.status === 403) alert('Invalid Role.');
    if (response.status === 401) throw response;
  } else {
    alert('Failed to fetch. Probably server is down.');
  }
  try {
    const result = await response.json();
    return result;
  } catch (e) {
    return null;
  }
};

//Short Aliases to Request calls:
const Get = async (endpoint: string, baseURL?: string) => await Request('GET', endpoint, null, baseURL);
const Post = async (endpoint: string, data?: any, baseURL?: string) => await Request('POST', endpoint, data, baseURL);
const Put = async (endpoint: string, data?: any, baseURL?: string) => await Request('PUT', endpoint, data, baseURL);
const Delete = async (endpoint: string, data?: any, baseURL?: string) => await Request('DELETE', endpoint, data, baseURL);

interface IConfigCRUDFactory {
  EndPoint: string;
  BaseURL?: string;
}

export class CRUDFactory {
  EndPoint: string;
  BaseURL: string;

  constructor({ EndPoint, BaseURL = AppConfig.BaseURL }: IConfigCRUDFactory) {
    this.EndPoint = EndPoint;
    this.BaseURL = BaseURL;
  }

  async InsertEntity(entity: any) {
    this.ADAPTER_OUT(entity);
    return await Post(this.EndPoint, entity, this.BaseURL)
      .then(r => this.UseCommonResponse(r))
      .catch(GeneralError);
  }

  async CreateAndCheckout(entity: any) {
    this.ADAPTER_OUT(entity);
    return await Post(this.EndPoint + '/CreateAndCheckout', entity, this.BaseURL)
      .then(r => this.UseCommonResponse(r))
      .catch(GeneralError);
  }

  async Checkout(entity: any) {
    return await Post(this.EndPoint + '/Checkout/' + entity.Id, entity, this.BaseURL)
      .then(r => this.UseCommonResponse(r))
      .catch(GeneralError);
  }

  async CancelCheckout(entity: any) {
    return await Post(this.EndPoint + '/CancelCheckout/' + entity.Id, entity, this.BaseURL)
      .then(r => this.UseCommonResponse(r))
      .catch(GeneralError);
  }

  async Checkin(entity: any) {
    this.ADAPTER_OUT(entity);
    return await Post(this.EndPoint + '/Checkin', entity, this.BaseURL)
      .then(r => this.UseCommonResponse(r))
      .catch(GeneralError);
  }

  async Finalize(entity: any) {
    this.ADAPTER_OUT(entity);
    return await Post(this.EndPoint + '/Finalize', entity, this.BaseURL)
      .then(r => this.UseCommonResponse(r))
      .catch(GeneralError);
  }

  async Unfinalize(entity: any) {
    this.ADAPTER_OUT(entity);
    return await Post(this.EndPoint + '/Unfinalize', entity, this.BaseURL)
      .then(r => this.UseCommonResponse(r))
      .catch(GeneralError);
  }

  async MakeRevision(entity: any) {
    return await Post(this.EndPoint + '/MakeRevision', entity, this.BaseURL)
      .then(r => this.UseCommonResponse(r))
      .catch(GeneralError);
  }

  async Duplicate(entity: any) {
    return await Post(this.EndPoint + '/Duplicate', entity, this.BaseURL)
      .then(r => this.UseCommonResponse(r))
      .catch(GeneralError);
  }

  async CreateInstance(entity: any) {
    return await Post(this.EndPoint + '/CreateInstance', entity, this.BaseURL)
      .then(r => this.UseCommonResponse(r))
      .catch(GeneralError);
  }

  async Get(operation: string) {
    return await Get(this.EndPoint + '/' + operation, this.BaseURL).catch(GeneralError);
  }

  async Post(operation: string, entity = {}) {
    return await Post(this.EndPoint + '/' + operation, entity, this.BaseURL).catch(GeneralError);
  }

  async CustomGet(operation: string) {
    return await Get(operation, this.BaseURL).catch(GeneralError);
  }

  async CustomPost(operation: string, entity = {}) {
    return await Post(operation, entity, this.BaseURL).catch(GeneralError);
  }

  async GetPaged(limit: number = 0, page: number = 1, params: string = '?') {
    return await Get(this.EndPoint + '/getPaged/' + limit + '/' + page + params, this.BaseURL)
      .then(r => this.UseCommonResponse(r, true))
      .catch(GeneralError);
  }

  async GetSingleWhere(property: string | null, value: string | null, params = '') {
    if (property && value) {
      return await Get(this.EndPoint + '/GetSingleWhere/' + property + '/' + value + '?' + params, this.BaseURL)
        .then(r => this.UseNudeResponse(r))
        .catch(GeneralError);
    } else if (params.length > 1) {
      return await Get(this.EndPoint + '/GetSingleWhere?' + params, this.BaseURL)
        .then(r => this.UseNudeResponse(r))
        .catch(GeneralError);
    } else {
      return Promise.reject('Invalid params for GetSingleWhere.');
    }
  }

  async LoadEntities(params = '?') {
    return await Get(this.EndPoint + params, this.BaseURL)
      .then(r => this.UseNudeResponse(r))
      .catch(GeneralError);
  }

  async LoadEntity(id: number) {
    if (id) {
      return await Get(this.EndPoint + '/' + id, this.BaseURL)
        .then(r => this.UseNudeResponse(r))
        .catch(GeneralError);
    } else {
      return Promise.reject('Id not found');
    }
  }

  async Remove(entity: any) {
    return await Delete(this.EndPoint, entity, this.BaseURL)
      .then(r => this.UseCommonResponse(r))
      .catch(GeneralError);
  }

  async RemoveById(id: number) {
    return await Delete(this.EndPoint + '/' + id, {}, this.BaseURL)
      .then(r => this.UseCommonResponse(r))
      .catch(GeneralError);
  }

  async Save(entity: any) {
    if (entity.Id > 0) {
      return await this.UpdateEntity(entity);
    } else {
      return await this.InsertEntity(entity);
    }
  }

  async SendTestEmail(entity: any) {
    return await Post(this.EndPoint + '/SendTestEmail', entity, this.BaseURL)
      .then(r => this.UseCommonResponse(r))
      .catch(GeneralError);
  }

  // async SetProperty(entity, sProperty, Value, qParams) {
  //   throw 'Not Implemented.';
  // }

  async UpdateEntity(entity: any) {
    this.ADAPTER_OUT(entity);
    return await Put(this.EndPoint, entity, this.BaseURL)
      .then(r => this.UseCommonResponse(r))
      .catch(GeneralError);
  }

  // // LOCAL OPERATIONS
  // getById(id) {
  //   for (let i = 0; i < this.arrAllRecords.length; i++) {
  //     if (id == this.arrAllRecords[i].Id) {
  //       return 0;
  //     }
  //     return null;
  //   }
  // }

  // getAll() {
  //   for (let i = 0; i < this.arrAllRecords.length; i++) {
  //     this.arrAllRecords[i] = this.arrAllRecords[i];
  //   }
  //   return this.arrAllRecords;
  // }

  // getRecursiveBySeedId() {}

  // getRawAll() {
  //   return this.arrAllRecords;
  // }

  // setRawAll(arr) {
  //   this.arrAllRecords = arr;
  // }

  // populateCatalogValues(entity:any) {
  //   for (let catalog in this.catalogs) {
  //     if (this.catalogs.hasOwnProperty(catalog)) {
  //       entity['' + catalog] = this.catalogs[catalog].getById(entity['' + catalog + 'Key']);
  //     }
  //   }
  // }

  UseCommonResponse = (response: any, returnCommonResponse?: boolean) => {
    //Make sure it is a commonResponse:
    if (!response || !response.hasOwnProperty('ErrorThrown')) throw response;

    //Check for Error:
    if (response.ErrorThrown) throw response;

    //Call Adapter In Hook:
    if (Array.isArray(response.Result)) {
      response.Result.forEach((entity: any) => {
        this.closeRevisions(entity);
        this.ADAPTER_IN(entity);
      });
    } else if (response.Result && typeof response.Result === 'object') {
      this.closeRevisions(response.Result);
      this.ADAPTER_IN(response.Result);
    }

    //Return complete CommonResponse:
    if (returnCommonResponse) return response;

    //Return only data:
    return response.Result;
  };

  UseNudeList = (response: any, boolWantCommonResponse: boolean) => {
    if (response.ErrorThrown) {
      throw response;
    }

    //Call Adapter In Hook:
    if (Array.isArray(response.Result)) {
      response.Result.forEach((entity: any) => {
        this.closeRevisions(entity);
        this.ADAPTER_IN(entity);
      });
    } else if (typeof response.Result === 'object') {
      this.closeRevisions(response.Result);
      this.ADAPTER_IN(response.Result);
    }

    //Return complete CommonResponse:
    if (boolWantCommonResponse) return response;

    //Return only data:
    return response.Result;
  };

  UseNudeResponse = (response: any) => {
    if (!response) return response;

    //Call Adapter In Hook:
    if (Array.isArray(response.Result)) {
      response.forEach((entity: any) => {
        this.closeRevisions(entity);
        this.ADAPTER_IN(entity);
      });
    } else if (typeof response === 'object') {
      this.closeRevisions(response);
      this.ADAPTER_IN(response);
    }

    //Return only data:
    return response;
  };

  closeRevisions = (entity: any) => {
    entity &&
      entity.Revisions &&
      entity.Revisions.forEach((revision: any) => {
        revision.isOpened = false;
      });
  };

  //Formatters:===================================================================
  formatDate = (date: any, format = 'M/D/YYYY') => {
    if (date) return moment(date).format(format);
  };

  formatDateMD = (date: any, format = 'MMMM Do, YYYY') => {
    if (date) return moment(date).format(format);
  };

  formatDateLG = (date: any, format = 'dddd, MMMM Do, YYYY') => {
    if (date) return moment(date).format(format);
  };

  // formatTime = (time: any, format = 'H:mm a') => {
  formatTime = (time: any, format = 'H:mm') => {
    if (time) return moment(time).format(format);
  };

  toServerDate = (date: any) => {
    var momentDate = moment(date || null);
    if (momentDate.isValid()) {
      momentDate.local();
      return momentDate.format();
    }
    return null;
  };

  toJavascriptDate = (from: any) => {
    return from ? new Date(from) : null;
  };

  formatCurrency = (number: number, decimals = 2) => {
    if (!isNaN(number) && number > 0) {
      return new Intl.NumberFormat('en-US', { maximumFractionDigits: decimals }).format(number);
    }
    return '';
  };

  toJSON = (from: any) => {
    if (from === undefined || from === '') return null;
    return JSON.parse(from);
  };

  //Hooks:=======================================================================
  ADAPTER_IN(entity: any) {
    return entity;
  }

  ADAPTER_OUT(entity: any) {
    return entity;
  }

  //Accounts:===================================================================
  async GetAccounts(params = '') {
    return await Get('Account?' + params, AppConfig.AuthURL).catch(GeneralError);
  }
}
